import React, { useState, useCallback, useEffect } from 'react';
import { useStyles } from './SessionsProcessLimits.styles';
import {
  AssessmentSessionProcess,
  PatchLimits,
  ProcessLimitRow,
  SessionsParamatersProcessLimitsKey,
} from './SessionsProcessLimits.types';
import { ActivateProcessLimits } from './Subcomponents/ActivateProcessLimits.component';
import { SessionProcessLimitField } from './Subcomponents/SessionProcessLimitField.component';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { changeProcessLimits } from '../../../../services/exams/assessmentSession/assessments.service';
import { getChangesFromArrayOfObject } from '../../../../utils/getChangesFromArrayOfObject';
import { InputTable } from '../../../../components/InputTable/InputTable.component';
import {
  InputTableColumns,
  ErrorList,
  UpHeaderColumn,
  inputTableBorder,
} from '../../../../components/InputTable/InputTable.type';
import { OverflowTypograpy } from '../../../../components/OverflowTypography/OverflowTypography.component';
import { AssessmentSessionState } from '../../../Dashboard/Dashboard.types';
import { useTranslation } from 'react-i18next';
import hasValueChanged from '../../../../utils/objectsComparer';

const columns: InputTableColumns[] = [
  {
    id: 'examTaskGroupName',
    label: '',
  },
  {
    id: 'isPZETrainingLimit',
    label: '',
    style: { borderLeft: inputTableBorder },
  },
  {
    id: 'trainingPZELimitValue',
    label: 'PZE',
  },
  {
    id: 'isExaminerTrainingLimit',
    label: '',
  },
  {
    id: 'trainingExaminerLimitValue',
    label: 'E',
  },
  {
    id: 'trainingZWOLimitValue',
    label: 'ZWO*',
  },
  {
    id: 'isPZEQualificationLimit',
    label: '',
    style: { borderLeft: inputTableBorder },
  },
  {
    id: 'qualificationPZElimitValue',
    label: 'PZE',
  },
  {
    id: 'isExaminerQualificationLimit',
    label: '',
  },
  {
    id: 'qualificationExaminerLimitValue',
    label: 'E',
  },
  {
    id: 'qualificationZWOlimitValue',
    label: 'ZWO*',
    style: { borderRight: inputTableBorder },
  },
  {
    id: 'isExaminerControlLimit',
    label: 'E',
  },
  {
    id: 'controlZWOLimitValue',
    label: 'ZWO*',
  },
];

interface SessionProcessLimitsProps {
  items: ProcessLimitRow[];
  actualProcessLimits: string;
  onClose: () => void;
  reload: () => void;
  isLoading: boolean;
  isExaminerView: boolean;
  onChange?: (isDataChanged: boolean) => void;
  refresh?: () => void;
}

export const SessionProcessLimits: React.FC<SessionProcessLimitsProps> = ({
  items,
  onClose,
  reload,
  actualProcessLimits,
  isLoading,
  onChange,
  isExaminerView,
  refresh,
}) => {
  const [invalidType, setInvalidType] = useState<ErrorList[]>([]);
  const [edited, setEdited] = useState<boolean>(false);
  const [parameters, setParameters] = useState<ProcessLimitRow[]>(items);
  const { openAlert } = useLayoutContext();
  const { t } = useTranslation();

  const upHeaderColumns: UpHeaderColumn[] = [
    {
      id: 'exercise',
      element: t('sessionsParameters:sessionProcessLimits.table.exercise'),
      style: { borderLeft: inputTableBorder },
    },
    {
      id: 'qualification',
      element: t('sessionsParameters:sessionProcessLimits.table.qualification'),
      style: { borderLeft: inputTableBorder, borderRight: inputTableBorder },
    },
    {
      id: 'control',
      element: t('sessionsParameters:sessionProcessLimits.table.control'),
    },
  ];

  const setProcess = (
    type: SessionsParamatersProcessLimitsKey,
    value: boolean,
    id: string | number,
    key: SessionsParamatersProcessLimitsKey,
    secondKey?: SessionsParamatersProcessLimitsKey,
    thirdKey?: SessionsParamatersProcessLimitsKey,
  ) => {
    const apiData: ProcessLimitRow[] = JSON.parse(actualProcessLimits);
    const initalValueControlZwo = apiData
      .filter((item) => item.examTaskGroupId === id)
      .map((i) => i.controlZWOLimitValue);
    setParameters((state: ProcessLimitRow[]) => {
      const updatedState = state.map((s: ProcessLimitRow) => {
        if (s.examTaskGroupId === id) {
          if (value) {
            s[key] = 1 as never;
            if (s.assessmentSessionStateId !== AssessmentSessionState.New) {
              s['controlZWOLimitValue'] = initalValueControlZwo[0] !== 0 ? initalValueControlZwo[0] : (1 as never);
            }
          } else {
            s[key] = 0 as never;
          }
          if (secondKey) {
            if (!s[secondKey] && !value) {
              if (thirdKey) {
                s[thirdKey] = 0 as never;
              }
            }
          }
          s[type] = value as never;
        }
        return s;
      });
      setEdited(!edited || hasValueChanged(updatedState, apiData));
      return updatedState;
    });
  };
  const clearTextFields = (type: string, value: boolean, id: string | number) => {
    switch (type) {
      case 'isPZETrainingLimit':
        setProcess(type, value, id, 'trainingPZELimitValue', 'isExaminerTrainingLimit', 'trainingZWOLimitValue');
        break;
      case 'isExaminerTrainingLimit':
        setProcess(type, value, id, 'trainingExaminerLimitValue', 'trainingPZELimitValue', 'trainingZWOLimitValue');
        break;
      case 'isPZEQualificationLimit':
        setProcess(
          type,
          value,
          id,
          'qualificationPZElimitValue',
          'isExaminerQualificationLimit',
          'qualificationZWOlimitValue',
        );
        break;
      case 'isExaminerQualificationLimit':
        setProcess(
          type,
          value,
          id,
          'qualificationExaminerLimitValue',
          'qualificationPZElimitValue',
          'qualificationZWOlimitValue',
        );
        break;
      case 'isExaminerControlLimit':
        setProcess(type, value, id, 'controlZWOLimitValue');
        break;
      default:
        return;
    }
  };
  const getKeyForCheckboxDisability = (
    key: SessionsParamatersProcessLimitsKey,
  ): SessionsParamatersProcessLimitsKey | undefined => {
    switch (key) {
      case 'controlZWOLimitValue':
        return 'isExaminerControlLimit';
      case 'qualificationZWOlimitValue':
        return 'isExaminerQualificationLimit';
      case 'trainingZWOLimitValue':
        return 'isExaminerTrainingLimit';
      default:
        return undefined;
    }
  };
  const handleValueCallback = useCallback(
    (key: SessionsParamatersProcessLimitsKey, value: number | null, id: string | number) => {
      setParameters((state: ProcessLimitRow[]) => {
        const updatedState = state.map((s: ProcessLimitRow) => {
          if (s.examTaskGroupId === id) {
            s[key] = value as never;
            if (key.includes('ZWO')) {
              const checkboxKey = getKeyForCheckboxDisability(key)!;
              s[checkboxKey] = (value !== 0) as never;
            }
          }
          return s;
        });
        const isEdited = hasValueChanged(updatedState, items);
        setEdited(isEdited);
        return updatedState;
      });
    },
    [parameters, setParameters, edited, setEdited, items],
  );

  const handleCheckboxChangeCallback = useCallback(
    (key: SessionsParamatersProcessLimitsKey, value: boolean, id: string | number) => {
      clearTextFields(key, value, id);
    },
    [parameters, setParameters, edited, setEdited],
  );
  const validate = (row: ProcessLimitRow): ErrorList[] => {
    const {
      examTaskGroupId,
      trainingPZELimitValue,
      trainingExaminerLimitValue,
      trainingZWOLimitValue,
      qualificationPZElimitValue,
      qualificationExaminerLimitValue,
      qualificationZWOlimitValue,
      controlZWOLimitValue,
      isExaminerControlLimit,
    } = row;

    const errorList: ErrorList[] = [];

    if (!(trainingPZELimitValue <= 10000))
      errorList.push({ type: 'trainingPZELimitValue', id: examTaskGroupId, value: trainingPZELimitValue });

    if (!(trainingExaminerLimitValue <= 10000))
      errorList.push({ type: 'trainingExaminerLimitValue', id: examTaskGroupId, value: trainingExaminerLimitValue });

    if (!(qualificationPZElimitValue <= 10000))
      errorList.push({ type: 'qualificationPZElimitValue', id: examTaskGroupId, value: qualificationPZElimitValue });

    if (!(qualificationExaminerLimitValue <= 10000))
      errorList.push({
        type: 'qualificationExaminerLimitValue',
        id: examTaskGroupId,
        value: qualificationExaminerLimitValue,
      });
    const isControlZWOlimitValid = isExaminerControlLimit
      ? controlZWOLimitValue >= 0 && controlZWOLimitValue <= 10000
      : true;

    if (!isControlZWOlimitValid)
      errorList.push({ type: 'controlZWOLimitValue', id: examTaskGroupId, value: controlZWOLimitValue });
    const isTrainingZWOlimitValid =
      trainingPZELimitValue > 0 || trainingExaminerLimitValue > 0
        ? trainingZWOLimitValue >= trainingPZELimitValue &&
          trainingZWOLimitValue >= trainingExaminerLimitValue &&
          trainingZWOLimitValue <= 10000
        : true;
    if (!isTrainingZWOlimitValid)
      errorList.push({ type: 'trainingZWOLimitValue', id: examTaskGroupId, value: trainingZWOLimitValue });

    const isQualificationZWOlimitValid =
      qualificationPZElimitValue > 0 || qualificationExaminerLimitValue > 0
        ? qualificationZWOlimitValue >= qualificationPZElimitValue &&
          qualificationZWOlimitValue >= qualificationExaminerLimitValue &&
          qualificationZWOlimitValue <= 10000
        : true;

    if (!isQualificationZWOlimitValid)
      errorList.push({ type: 'qualificationZWOlimitValue', id: examTaskGroupId, value: qualificationZWOlimitValue });

    return errorList;
  };

  const valueValidation = (limits: ProcessLimitRow): boolean => {
    const isValid = validate(limits);
    if (!isValid.length) {
      setInvalidType((prev) => prev.filter((e) => e.id !== limits.examTaskGroupId));
      return isValid.length > 0;
    } else {
      setInvalidType((prev) => [...prev, ...isValid]);
      return isValid.length > 0;
    }
  };

  const getLimitsByType = (type: SessionsParamatersProcessLimitsKey, data: ProcessLimitRow): boolean => {
    switch (type) {
      case 'isPZETrainingLimit':
        return data['trainingPZELimitValue'] > 0;
      case 'isExaminerTrainingLimit':
        return data['trainingExaminerLimitValue'] > 0;
      case 'isPZEQualificationLimit':
        return data['qualificationPZElimitValue'] > 0;
      case 'isExaminerQualificationLimit':
        return data['qualificationExaminerLimitValue'] > 0;
      case 'isExaminerControlLimit':
        return data['controlZWOLimitValue'] > 0;
      default:
        return false;
    }
  };

  const checkIfDisabled = (type: SessionsParamatersProcessLimitsKey, data: ProcessLimitRow): boolean | undefined => {
    switch (type) {
      case 'trainingZWOLimitValue':
        return data['trainingPZELimitValue'] === 0 && data['trainingExaminerLimitValue'] === 0;
      case 'qualificationZWOlimitValue':
        return data['qualificationPZElimitValue'] === 0 && data['qualificationExaminerLimitValue'] === 0;
      case 'controlZWOLimitValue':
        return data['isExaminerControlLimit'] === false;
    }
  };
  const getRowComponent = (type: string, data: ProcessLimitRow, isValid: boolean): React.ReactNode | string | null => {
    switch (type) {
      case 'examTaskGroupName':
        return (
          <OverflowTypograpy
            style={{
              display: 'inline-block',
              fontWeight: 'bolder',
              fontSize: '1rem',
              color: '#3C419A',
              width: '160px',
              marginTop: 14,
              marginBottom: 10,
            }}
          >
            {data[type]}
          </OverflowTypograpy>
        );
      case 'isPZETrainingLimit':
      case 'isExaminerTrainingLimit':
      case 'isPZEQualificationLimit':
      case 'isExaminerQualificationLimit':
      case 'isExaminerControlLimit':
        const countLimit = getLimitsByType(type, data);
        return (
          <ActivateProcessLimits
            handleCheckboxChange={handleCheckboxChangeCallback}
            isChecked={countLimit}
            type={type}
            value={data}
            validation={valueValidation}
          />
        );
      case 'trainingPZELimitValue':
      case 'trainingZWOLimitValue':
      case 'trainingExaminerLimitValue':
      case 'qualificationPZElimitValue':
      case 'qualificationExaminerLimitValue':
      case 'qualificationZWOlimitValue':
      case 'controlZWOLimitValue':
        const isRequired = () => {
          if (type.includes('ZWO')) {
            if (isValid) {
              return data[type] > 0 ? false : true;
            } else return false;
          } else return false;
        };

        const isDisabled = () => {
          if (data.assessmentSessionStateId !== AssessmentSessionState.New && data[type] === 0) return true;
          if (type.includes('ZWO')) {
            return checkIfDisabled(type, data);
          } else return false;
        };

        const getAriaLabel = (type: string, examTaskGroupName: string) => {
          switch (type) {
            case 'trainingPZELimitValue':
              return `Liczba RGZ do oceny w procesie ćwiczeniowym przez PZE dla grupy zadań ${examTaskGroupName}`;
            case 'trainingExaminerLimitValue':
              return `Liczba RGZ do oceny w procesie ćwiczeniowym przez E dla grupy zadań ${examTaskGroupName}`;
            case 'trainingZWOLimitValue':
              return `Liczba RGZ do przygotowania dla procesu ćwiczeniowego przez ZWO dla grupy zadań ${examTaskGroupName}`;
            case 'qualificationPZElimitValue':
              return `Liczba RGZ do oceny w procesie kwalifikacji przez PZE dla grupy zadań ${examTaskGroupName}`;
            case 'qualificationExaminerLimitValue':
              return `Liczba RGZ do oceny w procesie kwalifikacji przez E dla grupy zadań ${examTaskGroupName}`;
            case 'qualificationZWOlimitValue':
              return `Liczba RGZ do przygotowania dla procesu kwalifikacji przez ZWO dla grupy zadań ${examTaskGroupName}`;
            case 'controlZWOLimitValue':
              return `Liczba RGZ do przygotowania dla procesu kontrolnego przez ZWO dla grupy zadań ${examTaskGroupName}`;
          }
        };

        return (
          <SessionProcessLimitField
            value={data}
            initialValue={data[type]}
            isValid={isValid}
            handleValueChange={handleValueCallback}
            validation={valueValidation}
            type={type}
            isRequired={isRequired()}
            isDisabled={isDisabled()}
            ariaLabel={getAriaLabel(type, data.examTaskGroupName)}
          />
        );
    }
  };

  const getValidationMessage = (error: ErrorList): string => {
    const message = 'Pole może zawierać tylko liczby z przedziału 1 - 10 000';
    if (error.type.includes('PZE') || error.type.includes('Examiner')) {
      return message;
    } else {
      if ((error.value && (error.value as number) > 0 && (error.value as number) <= 10000) || error.value === 0) {
        return 'Wartość ZWO musi być większa lub równa liczbie RGZ dla E/PZE';
      } else {
        return message;
      }
    }
  };

  const handleDiscardCallback = useCallback(() => {
    onClose();
  }, [onClose]);

  const checkRequired = (isInvalidRow: ErrorList | undefined): boolean => {
    return isInvalidRow === undefined;
  };

  const handleChangeLimits = () => {
    const apiData: ProcessLimitRow[] = JSON.parse(actualProcessLimits);
    const filterChanges: ProcessLimitRow[] = getChangesFromArrayOfObject<ProcessLimitRow>(apiData, parameters);
    const collectionArray: AssessmentSessionProcess[] = [];
    filterChanges.forEach((e: ProcessLimitRow) => {
      const training: AssessmentSessionProcess = {
        assessmentSessionProcessId: e.trainingProcessId,
        examiningTeamChairmanRequiredSolutionsCount: e.trainingPZELimitValue ? e.trainingPZELimitValue : null,
        examingTeamMemberRequiredSolutionsCount: e.trainingExaminerLimitValue ? e.trainingExaminerLimitValue : null,
        sampleAssessmentGroupMemberRequiredSolutionsCount: e.trainingZWOLimitValue ? e.trainingZWOLimitValue : null,
      };
      const qualification: AssessmentSessionProcess = {
        assessmentSessionProcessId: e.qualificationProcessId,
        examiningTeamChairmanRequiredSolutionsCount: e.qualificationPZElimitValue ? e.qualificationPZElimitValue : null,
        examingTeamMemberRequiredSolutionsCount: e.qualificationExaminerLimitValue
          ? e.qualificationExaminerLimitValue
          : null,
        sampleAssessmentGroupMemberRequiredSolutionsCount: e.qualificationZWOlimitValue
          ? e.qualificationZWOlimitValue
          : null,
      };
      const control: AssessmentSessionProcess = {
        assessmentSessionProcessId: e.controlProcessId,
        sampleAssessmentGroupMemberRequiredSolutionsCount: e.controlZWOLimitValue ? e.controlZWOLimitValue : null,
      };
      collectionArray.push({ ...training });
      collectionArray.push({ ...qualification });
      collectionArray.push({ ...control });
    });

    const body: PatchLimits = { assessmentSessionProcesses: collectionArray };

    changeProcessLimits(body, isExaminerView)
      .then(() => {
        setEdited(false);
        reload();
        refresh && refresh();
        setTimeout(() => openAlert('success'), 500);
      })

      .catch(() => {
        openAlert('error');
      });
  };

  const emptyMessage = 'Brak wyników';

  useEffect(() => {
    onChange && onChange(edited);
  }, [edited]);

  return (
    <InputTable<ProcessLimitRow>
      columns={columns}
      upHeaderColumns={upHeaderColumns}
      parameters={parameters}
      items={items}
      keyParam={'examTaskGroupId'}
      invalidType={invalidType}
      headerText={t('sessionsParameters:sessionProcessLimits.header')}
      onCancel={handleDiscardCallback}
      onSave={handleChangeLimits}
      getValidationMessage={getValidationMessage}
      getRowComponent={getRowComponent}
      edited={edited}
      emptyMessage={emptyMessage}
      checkRequired={checkRequired}
      isLoading={isLoading}
    />
  );
};
